import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from '../lib/api';

/**
 * Image display configuration editor used on both Partner and Tenant edit
 * screens. Mirrors {@link RepeatCadenceEditor} conceptually: tri-state
 * controls so each row can be ON, OFF, or "inherit" (let the next layer
 * decide).
 *
 * Inheritance model (resolved server-side in
 * nova-poller/src/imageDisplay.ts):
 *
 *   tenant override  ->  partner default  ->  built-in system default
 *
 * On the partner page we render the partner default directly (placeholder =
 * system default). On the tenant page we accept `partnerDefaults` so empty
 * rows can show the value the tenant would inherit from its partner if no
 * tenant override is set.
 */

type BooleanKey =
  | 'show_wetness_percent'
  | 'show_battery'
  | 'show_diaper_age_ring'
  | 'show_diaper_age_text'
  | 'show_diaper_model'
  | 'show_device_name'
  | 'show_signal_strength';

const BOOLEAN_KEYS: readonly BooleanKey[] = [
  'show_wetness_percent',
  'show_battery',
  'show_diaper_age_ring',
  'show_diaper_age_text',
  'show_diaper_model',
  'show_device_name',
  'show_signal_strength',
];

const LANGUAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'en', label: 'English (en)' },
  { value: 'da', label: 'Danish (da)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'de', label: 'German (de)' },
];

/** Full resolved config shape (mirrors ImageDisplayConfig in the poller). */
export interface ResolvedImageDisplayConfig {
  show_wetness_percent: boolean;
  show_battery: boolean;
  show_diaper_age_ring: boolean;
  show_diaper_age_text: boolean;
  show_diaper_model: boolean;
  show_device_name: boolean;
  show_signal_strength: boolean;
  language_override: string | null;
}

/**
 * Editor value.
 *  - `overrides` holds only the keys the current layer explicitly sets.
 *    Missing keys = "inherit from the next layer". Mirrors the tenant/partner
 *    JSONB shape in the DB.
 *  - `inheritAll` (tenant-only) mirrors the DB's `NULL` value for the tenant
 *    column: the tenant is completely transparent and every value comes from
 *    the owning partner (then system).
 */
export interface ImageDisplayValue {
  overrides: Partial<ResolvedImageDisplayConfig>;
  inheritAll?: boolean;
}

interface DefaultsResponse {
  source: string;
  defaults: ResolvedImageDisplayConfig;
  supportedLanguages: readonly string[];
}

interface Props {
  value: ImageDisplayValue;
  onChange: (v: ImageDisplayValue) => void;
  /**
   * Partner-level defaults passed in on the tenant page. Undefined on the
   * partner page (placeholders fall through to system only).
   */
  partnerDefaults?: ResolvedImageDisplayConfig;
  /** Display name for the parent layer (e.g. partner name). */
  parentLabel?: string;
  /** Allow the tenant-only "inherit everything from partner" top-level toggle. */
  allowInheritAll?: boolean;
}

const LABELS: Record<BooleanKey, { label: string; help?: string }> = {
  show_wetness_percent: {
    label: 'Wetness percentage',
    help: 'Off by default — the number can be noisy for non-clinical viewers.',
  },
  show_battery: { label: 'Clip battery %' },
  show_diaper_age_ring: {
    label: 'Diaper-age progress ring',
    help: 'The circular progress arc around the diaper icon.',
  },
  show_diaper_age_text: {
    label: 'Diaper-age text (e.g. "2h 15m")',
  },
  show_diaper_model: { label: 'Diaper model' },
  show_device_name: { label: 'Client / device name' },
  show_signal_strength: {
    label: 'Signal strength',
    help: 'Reserved — the poller does not emit this metric yet; flag exists so you can pre-configure it.',
  },
};

export const ImageDisplayEditor: React.FC<Props> = ({
  value,
  onChange,
  partnerDefaults,
  parentLabel,
  allowInheritAll,
}) => {
  const [systemDefaults, setSystemDefaults] = useState<ResolvedImageDisplayConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const parentName = parentLabel || 'partner default';

  useEffect(() => {
    let cancelled = false;
    apiCall('/api/configuration/image-display-defaults')
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: DefaultsResponse = await resp.json();
        if (!cancelled) setSystemDefaults(data.defaults);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || String(err));
      });
    return () => { cancelled = true; };
  }, []);

  const overrides = useMemo(() => value.overrides || {}, [value.overrides]);
  const inheritAll = !!value.inheritAll;

  /**
   * Resolve the effective value for a key using the same precedence as the
   * server: own override -> partner default -> system default.
   */
  const resolveEffective = useCallback(<K extends keyof ResolvedImageDisplayConfig>(
    key: K,
  ): { value: ResolvedImageDisplayConfig[K]; source: 'override' | 'partner' | 'system' } | null => {
    if (!inheritAll && key in overrides) {
      return { value: overrides[key] as ResolvedImageDisplayConfig[K], source: 'override' };
    }
    if (partnerDefaults && partnerDefaults[key] !== undefined) {
      return { value: partnerDefaults[key] as ResolvedImageDisplayConfig[K], source: 'partner' };
    }
    if (systemDefaults && systemDefaults[key] !== undefined) {
      return { value: systemDefaults[key] as ResolvedImageDisplayConfig[K], source: 'system' };
    }
    return null;
  }, [inheritAll, overrides, partnerDefaults, systemDefaults]);

  const setBoolOverride = (key: BooleanKey, next: 'on' | 'off' | 'inherit') => {
    const nextOverrides = { ...overrides };
    if (next === 'inherit') {
      delete nextOverrides[key];
    } else {
      nextOverrides[key] = next === 'on';
    }
    onChange({ ...value, overrides: nextOverrides, inheritAll: false });
  };

  const setLanguageOverride = (next: string | null | '__inherit__') => {
    const nextOverrides = { ...overrides };
    if (next === '__inherit__') {
      delete nextOverrides.language_override;
    } else {
      nextOverrides.language_override = next;
    }
    onChange({ ...value, overrides: nextOverrides, inheritAll: false });
  };

  const resetAll = () => {
    onChange({ overrides: {}, inheritAll: false });
  };

  const setInheritAll = (checked: boolean) => {
    onChange({ overrides: checked ? {} : (value.overrides || {}), inheritAll: checked });
  };

  const sourceBadge = (src: 'override' | 'partner' | 'system') => {
    if (src === 'override') return { text: 'override', cls: 'text-emerald-700' };
    if (src === 'partner') return { text: parentName, cls: 'text-gray-500 italic' };
    return { text: 'system default', cls: 'text-gray-500 italic' };
  };

  const effectiveLanguage = useMemo(() => resolveEffective('language_override'), [resolveEffective]);

  return (
    <div className="space-y-4">
      {loadError && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Could not load image display system defaults ({loadError}). Inputs still work; inheritance hints may be missing.
        </div>
      )}

      {allowInheritAll && (
        <div className="flex items-center justify-between gap-3 border-b pb-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={inheritAll}
              onChange={(e) => setInheritAll(e.target.checked)}
            />
            Fully inherit image display config from {parentName}
          </label>
          {!inheritAll && Object.keys(overrides).length > 0 && (
            <button
              type="button"
              onClick={resetAll}
              className="text-xs text-blue-600 hover:underline"
              title="Clear every override on this tenant"
            >
              Reset all overrides
            </button>
          )}
        </div>
      )}

      {!allowInheritAll && Object.keys(overrides).length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={resetAll}
            className="text-xs text-blue-600 hover:underline"
            title="Clear every override and use system defaults"
          >
            Reset all to system defaults
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {BOOLEAN_KEYS.map((key) => {
          const eff = resolveEffective(key);
          const { label, help } = LABELS[key];
          const hasOverride = !inheritAll && key in overrides;
          const current: 'on' | 'off' | 'inherit' = hasOverride
            ? (overrides[key] ? 'on' : 'off')
            : 'inherit';
          const badge = eff ? sourceBadge(eff.source) : null;

          return (
            <div key={key} className="border rounded-lg px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700">{label}</label>
                  {help && <p className="text-[11px] text-gray-400 mt-0.5">{help}</p>}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1">
                <SegmentButton
                  active={current === 'on'}
                  onClick={() => setBoolOverride(key, 'on')}
                  disabled={inheritAll}
                >
                  On
                </SegmentButton>
                <SegmentButton
                  active={current === 'off'}
                  onClick={() => setBoolOverride(key, 'off')}
                  disabled={inheritAll}
                >
                  Off
                </SegmentButton>
                <SegmentButton
                  active={current === 'inherit'}
                  onClick={() => setBoolOverride(key, 'inherit')}
                  disabled={inheritAll}
                >
                  Inherit
                </SegmentButton>
              </div>
              {eff && badge && (
                <p className={`text-[11px] mt-1 ${badge.cls}`}>
                  Effective: {eff.value ? 'on' : 'off'} · {badge.text}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Language override is a select, not a tri-state, because the
          "natural" value is already null (= let renderer default). */}
      <div className="border rounded-lg px-3 py-2">
        <label className="block text-xs font-medium text-gray-700">Force language</label>
        <p className="text-[11px] text-gray-400 mt-0.5">
          When set, the image generator receives <code className="font-mono">lang=&lt;code&gt;</code>. Leave on
          "inherit" to let the next layer decide; "Renderer default" explicitly clears any inherited value.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <select
            className="border rounded-lg px-3 py-1.5 text-sm"
            value={
              inheritAll || !('language_override' in overrides)
                ? '__inherit__'
                : (overrides.language_override === null ? '__default__' : (overrides.language_override as string))
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__inherit__') setLanguageOverride('__inherit__');
              else if (v === '__default__') setLanguageOverride(null);
              else setLanguageOverride(v);
            }}
            disabled={inheritAll}
          >
            <option value="__inherit__">Inherit from {parentName}</option>
            <option value="__default__">Renderer default (English)</option>
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {effectiveLanguage && (
          <p className={`text-[11px] mt-1 ${sourceBadge(effectiveLanguage.source).cls}`}>
            Effective: {effectiveLanguage.value ?? 'renderer default'} · {sourceBadge(effectiveLanguage.source).text}
          </p>
        )}
      </div>
    </div>
  );
};

const SegmentButton: React.FC<{
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ active, onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={
      `text-xs px-2.5 py-1 rounded border transition-colors ` +
      (disabled
        ? 'border-gray-200 text-gray-300 cursor-not-allowed '
        : active
          ? 'border-blue-500 bg-blue-50 text-blue-700 '
          : 'border-gray-300 text-gray-600 hover:bg-gray-50 ')
    }
  >
    {children}
  </button>
);

/**
 * Translate an editor value into the JSON the REST endpoints expect.
 *
 * Partners reject `null` (they are the root; use `{}` to fully inherit from
 * system). Tenants allow `null` to mean "fully inherit from owner partner".
 */
export function serializeImageDisplayForPartner(v: ImageDisplayValue): Record<string, any> {
  return { ...(v.overrides || {}) };
}

export function serializeImageDisplayForTenant(v: ImageDisplayValue): Record<string, any> | null {
  if (v.inheritAll) return null;
  return { ...(v.overrides || {}) };
}

/**
 * Inverse of the serialize helpers — build an editor value from the JSON
 * the GET endpoint returned.
 */
export function deserializeImageDisplay(
  raw: Record<string, any> | null | undefined,
  opts: { tenantMode: boolean },
): ImageDisplayValue {
  if (opts.tenantMode && raw == null) {
    return { overrides: {}, inheritAll: true };
  }
  return { overrides: { ...(raw || {}) }, inheritAll: false };
}
